import { useEffect, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/ui/Button.jsx'
import Icon from '../../../components/ui/Icon.jsx'
import { formatCurrency } from '../../../lib/format.js'
import api from '../../../lib/api.js'

/*
 * Payment step (paid plans) — inventory §5/§6. GET /auth/register/setup-intent
 * yields a Stripe SetupIntent client_secret + publishable_key; we confirm the
 * card with Stripe Elements, then POST the payment_method to
 * /auth/register/complete-payment-setup which advances to pending_documents.
 */
export default function PaymentStep({ t, plan, onBack, onDone }) {
  const { i18n } = useTranslation()
  const [intent, setIntent] = useState(null) // { client_secret, publishable_key }
  const [stripePromise, setStripePromise] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get('/auth/register/setup-intent')
      .then(({ data }) => { setIntent(data); if (data?.publishable_key) setStripePromise(loadStripe(data.publishable_key, { locale: i18n.resolvedLanguage })) })
      .catch((e) => setError(e?.response?.data?.error || t('register.payment.intentError')))
  }, [i18n.resolvedLanguage, t])

  return (
    <div>
      <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, color: 'var(--color-ink)', margin: '6px 0 12px' }}>{t('register.payment.title')}</h3>
      <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 16 }}>{t('register.payment.intro')}</p>

      {plan && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--color-bg-lilac)', borderRadius: 12, padding: '12px 16px', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, color: 'var(--color-ink)' }}>{plan.name}</span>
          <span style={{ fontFamily: 'var(--font-title)', fontWeight: 700, color: 'var(--color-ink)' }}>{formatCurrency(plan.price ?? plan.monthly_base_price)}<span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-muted)' }}> / {plan.billing_period === 'yearly' ? t('register.year') : t('register.month')}</span></span>
        </div>
      )}

      {error && <Banner tone="error" text={error} />}

      {stripePromise && intent ? (
        <Elements stripe={stripePromise} options={{ locale: i18n.resolvedLanguage }}>
          <CardForm t={t} clientSecret={intent.client_secret} onBack={onBack} onDone={onDone} />
        </Elements>
      ) : !error ? (
        <div style={{ color: 'var(--color-text-muted)', fontSize: 13.5, padding: '10px 0' }}>{t('register.payment.loadingStripe')}</div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <Button variant="secondary" onClick={onBack}>{t('register.back')}</Button>
        </div>
      )}
    </div>
  )
}

function CardForm({ t, clientSecret, onBack, onDone }) {
  const stripe = useStripe()
  const elements = useElements()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setBusy(true); setError('')
    try {
      const { error: stripeErr, setupIntent } = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: elements.getElement(CardElement) } })
      if (stripeErr) { setError(stripeErr.message || t('register.payment.cardError')); return }
      await api.post('/auth/register/complete-payment-setup', { payment_method_id: setupIntent.payment_method })
      onDone()
    } catch (err) { setError(err?.response?.data?.error || t('register.payment.cardError')) } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit}>
      <div style={{ border: '1.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-field)', padding: '14px 14px', marginBottom: 12 }}>
        <CardElement options={{ style: { base: { fontSize: '15px', color: '#2A1A4A', '::placeholder': { color: '#9a93ad' } } }, hidePostalCode: false } } />
      </div>
      {error && <Banner tone="error" text={error} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-muted)', fontSize: 12.5, marginBottom: 18 }}>
        <Icon name="shield" size={15} /> {t('register.payment.secure')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button type="button" variant="secondary" onClick={onBack}>{t('register.back')}</Button>
        <Button type="submit" variant="primary" icon="chevron-right" loading={busy} disabled={!stripe}>{t('register.continue')}</Button>
      </div>
    </form>
  )
}

function Banner({ tone, text }) {
  const danger = tone === 'error'
  return <div role="alert" style={{ background: danger ? 'var(--color-danger-bg)' : 'var(--color-success-bg)', color: danger ? 'var(--color-danger)' : 'var(--color-success)', border: `1px solid ${danger ? '#f3c9c9' : '#bfe3cf'}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{text}</div>
}
